import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Box, Container, FormControl, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme, } from "@mui/material";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useRetrievePlayersByTeamId, useRetrieveTeam, useRetrieveUserTeamPermissions, } from "../../ApiProvider";
import { ErrorView } from "../../nav/Error";
import { Forbidden } from "../../nav/Forbidden";
import { Loading } from "../../nav/Loading";
import { Bench } from "./Bench";
import { FormationPickerDrawer } from "./FormationPickerDrawer";
import { Pitch } from "./Pitch";
import { defaultFormationForTeamSize, slotsForFormation } from "./slots";
import { FORMATIONS_BY_TEAM_SIZE, TEAM_SIZES, } from "./types";
const playerFirstName = (player) => {
    const model = player.user ?? player.externalUser ?? null;
    return model?.firstName ?? "";
};
const playerLastName = (player) => {
    const model = player.user ?? player.externalUser ?? null;
    return model?.lastName ?? "";
};
const toFormationPlayer = (member) => ({
    id: member.id,
    squadNumber: member.squadNumber ?? null,
    firstName: playerFirstName(member),
    lastName: playerLastName(member),
    primaryPosition: member.positionOne ?? null,
});
const emptyAssignments = (slotIds) => slotIds.reduce((acc, id) => {
    acc[id] = null;
    return acc;
}, {});
export const FormationPage = () => {
    const { id } = useParams();
    const teamId = id ? Number(id) : undefined;
    const [teamSize, setTeamSize] = useState(11);
    const [formation, setFormation] = useState(defaultFormationForTeamSize(11));
    const slots = useMemo(() => slotsForFormation(formation), [formation]);
    const slotIds = useMemo(() => slots.map((s) => s.id), [slots]);
    const [assignments, setAssignments] = useState(() => emptyAssignments(slotIds));
    const [selected, setSelected] = useState(null);
    const [pickerSlotId, setPickerSlotId] = useState(null);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { data: permissions, isLoading: isPermsLoading, error: permsError, } = useRetrieveUserTeamPermissions({ teamId });
    const { canView = false } = permissions || {};
    const { data: team, isLoading: isTeamLoading, error: teamError, } = useRetrieveTeam({ id: teamId, enabled: canView });
    const { data: members = [], isLoading: isMembersLoading, error: membersError, } = useRetrievePlayersByTeamId({ teamId, enabled: canView });
    // Only active squad members are selectable — inactive players shouldn't
    // appear on the bench or be assignable to the pitch.
    const allPlayers = useMemo(() => members.filter((m) => m.isActive).map(toFormationPlayer), [members]);
    const assignedIds = useMemo(() => new Set(Object.values(assignments)
        .filter((p) => p !== null)
        .map((p) => p.id)), [assignments]);
    const benchPlayers = useMemo(() => allPlayers.filter((p) => !assignedIds.has(p.id)), [allPlayers, assignedIds]);
    const handleSizeChange = (_, value) => {
        if (value === null)
            return;
        const nextFormation = defaultFormationForTeamSize(value);
        setTeamSize(value);
        setFormation(nextFormation);
        setAssignments(emptyAssignments(slotsForFormation(nextFormation).map((s) => s.id)));
        setSelected(null);
        setPickerSlotId(null);
    };
    const handleFormationChange = (event) => {
        const next = event.target.value;
        setFormation(next);
        setAssignments(emptyAssignments(slotsForFormation(next).map((s) => s.id)));
        setSelected(null);
        setPickerSlotId(null);
    };
    const handleSlotDrop = (item, targetSlotId) => {
        setAssignments((prev) => {
            const next = { ...prev };
            const occupant = prev[targetSlotId] ?? null;
            if (item.source === "bench") {
                next[targetSlotId] = item.player;
            }
            else {
                next[targetSlotId] = item.player;
                next[item.slotId] = occupant;
            }
            return next;
        });
    };
    const handleBenchDrop = (item) => {
        if (item.source !== "pitch")
            return;
        setAssignments((prev) => ({ ...prev, [item.slotId]: null }));
    };
    // Dropping a pitch player onto a specific bench player → they swap places:
    // the bench player takes over the pitch slot, the pitch player goes to the
    // bench. Implemented by just replacing the slot's occupant; the original
    // pitch player drops out of assignments and reappears on the bench.
    const handleBenchPlayerSwap = (pitchSlotId, benchPlayer) => {
        setAssignments((prev) => ({ ...prev, [pitchSlotId]: benchPlayer }));
    };
    const isSameTarget = (sel, target) => {
        if (!sel)
            return false;
        if (target.kind === "pitchSlot")
            return sel.source === "pitch" && sel.slotId === target.slotId;
        if (target.kind === "benchPlayer")
            return sel.source === "bench" && sel.playerId === target.player.id;
        return false;
    };
    const selectionToDragItem = (sel) => {
        if (!sel)
            return null;
        if (sel.source === "pitch") {
            const player = assignments[sel.slotId];
            if (!player)
                return null;
            return { source: "pitch", player, slotId: sel.slotId };
        }
        const player = allPlayers.find((p) => p.id === sel.playerId);
        if (!player)
            return null;
        return { source: "bench", player };
    };
    // On mobile, tapping a pitch slot opens the picker drawer instead of
    // running the desktop click-select flow.
    const handlePitchSlotClick = (slotId, player) => {
        if (isMobile) {
            setSelected(null);
            setPickerSlotId(slotId);
            return;
        }
        handleClick({ kind: "pitchSlot", slotId, player });
    };
    const handleDrawerSelectPlayer = (player) => {
        if (pickerSlotId === null)
            return;
        handleSlotDrop({ source: "bench", player }, pickerSlotId);
        setPickerSlotId(null);
    };
    const handleDrawerRemove = () => {
        if (pickerSlotId === null)
            return;
        setAssignments((prev) => ({ ...prev, [pickerSlotId]: null }));
        setPickerSlotId(null);
    };
    const handleClick = (target) => {
        if (!selected) {
            if (target.kind === "pitchSlot" && target.player) {
                setSelected({
                    source: "pitch",
                    slotId: target.slotId,
                    playerId: target.player.id,
                });
            }
            else if (target.kind === "benchPlayer") {
                setSelected({ source: "bench", playerId: target.player.id });
            }
            return;
        }
        if (isSameTarget(selected, target)) {
            setSelected(null);
            return;
        }
        const dragItem = selectionToDragItem(selected);
        if (!dragItem) {
            setSelected(null);
            return;
        }
        if (target.kind === "pitchSlot") {
            handleSlotDrop(dragItem, target.slotId);
            setSelected(null);
        }
        else if (target.kind === "benchPlayer") {
            if (dragItem.source === "pitch") {
                handleBenchPlayerSwap(dragItem.slotId, target.player);
                setSelected(null);
            }
            else {
                setSelected({ source: "bench", playerId: target.player.id });
            }
        }
        else {
            if (dragItem.source === "pitch")
                handleBenchDrop(dragItem);
            setSelected(null);
        }
    };
    if (isPermsLoading) {
        return _jsx(Loading, {});
    }
    if (permsError) {
        return _jsx(ErrorView, { error: permsError });
    }
    if (!canView) {
        return _jsx(Forbidden, {});
    }
    if (isTeamLoading || isMembersLoading) {
        return _jsx(Loading, {});
    }
    if (teamError || membersError || !team) {
        return (_jsx(ErrorView, { error: teamError ??
                membersError ??
                new Error("Team not found") }));
    }
    const primaryColour = team.primaryColour ?? "#1d4ed8";
    const formationOptions = FORMATIONS_BY_TEAM_SIZE[teamSize];
    // Single source of truth for the pitch row's geometry on sm+. Width is
    // derived from the same height so the pitch and bench wrappers share an
    // exact, definite pixel height (no aspect-ratio rounding mismatch).
    const PITCH_HEIGHT_SM = "calc(100vh - 72px)";
    const PITCH_WIDTH_SM = `calc((100vh - 72px) * 2 / 3)`;
    return (_jsxs(Container, { maxWidth: "lg", sx: { py: 2 }, children: [_jsxs(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "minmax(180px, 220px) auto minmax(200px, 260px)",
                    },
                    gridTemplateAreas: {
                        xs: `"controls" "pitch"`,
                        sm: `"controls pitch bench"`,
                    },
                    gridTemplateRows: { sm: PITCH_HEIGHT_SM },
                    gap: 2,
                    alignItems: "stretch",
                    justifyContent: { sm: "center" },
                }, children: [_jsxs(Box, { sx: {
                            gridArea: "controls",
                            display: "flex",
                            flexDirection: { xs: "row", sm: "column" },
                            alignItems: { xs: "center", sm: "stretch" },
                            flexWrap: "wrap",
                            gap: 1.5,
                        }, children: [_jsxs(Typography, { variant: "h6", component: "h1", sx: {
                                    fontWeight: 700,
                                    lineHeight: 1.2,
                                    width: { xs: "100%", sm: "auto" },
                                    mb: { xs: 0, sm: 0.5 },
                                }, children: [team.name, " \u2014 Formation"] }), _jsxs(Box, { sx: { width: { xs: "auto", sm: "100%" } }, children: [_jsx(Typography, { variant: "caption", color: "text.secondary", sx: {
                                            display: { xs: "none", sm: "block" },
                                            mb: 0.5,
                                            lineHeight: 1,
                                        }, children: "Team size" }), _jsx(ToggleButtonGroup, { value: teamSize, exclusive: true, onChange: handleSizeChange, size: "small", "aria-label": "Team size", children: TEAM_SIZES.map((size) => (_jsx(ToggleButton, { value: size, "aria-label": `${size}-a-side`, sx: { px: 1.5, py: 0.25, fontSize: "0.8rem" }, children: size }, size))) })] }), _jsxs(FormControl, { size: "small", sx: { minWidth: 140, width: { xs: "auto", sm: "100%" } }, children: [_jsx(InputLabel, { id: "formation-label", children: "Formation" }), _jsx(Select, { labelId: "formation-label", id: "formation", value: formation, label: "Formation", onChange: handleFormationChange, children: formationOptions.map((f) => (_jsx(MenuItem, { value: f, children: f }, f))) })] })] }), _jsx(Box, { sx: {
                            gridArea: "pitch",
                            width: { xs: "100%", sm: PITCH_WIDTH_SM },
                            height: { xs: "auto", sm: PITCH_HEIGHT_SM },
                            aspectRatio: { xs: "2 / 3", sm: "unset" },
                            justifySelf: { sm: "center" },
                        }, children: _jsx(Pitch, { slots: slots, assignments: assignments, primaryColour: primaryColour, onDrop: handleSlotDrop, selectedSlotId: selected?.source === "pitch" ? selected.slotId : null, onClickSlot: handlePitchSlotClick }) }), !isMobile && (_jsx(Box, { sx: {
                            gridArea: "bench",
                            minWidth: 0,
                            height: PITCH_HEIGHT_SM,
                            display: "flex",
                        }, children: _jsx(Bench, { players: benchPlayers, primaryColour: primaryColour, onDrop: handleBenchDrop, onSwapBenchPlayer: handleBenchPlayerSwap, selectedPlayerId: selected?.source === "bench" ? selected.playerId : null, onClickPlayer: (player) => handleClick({ kind: "benchPlayer", player }), onClickArea: () => handleClick({ kind: "benchArea" }) }) }))] }), _jsx(FormationPickerDrawer, { open: pickerSlotId !== null, slot: slots.find((s) => s.id === pickerSlotId) ?? null, currentPlayer: pickerSlotId ? assignments[pickerSlotId] ?? null : null, benchPlayers: benchPlayers, primaryColour: primaryColour, onClose: () => setPickerSlotId(null), onSelectBenchPlayer: handleDrawerSelectPlayer, onRemoveCurrent: handleDrawerRemove })] }));
};
